import { useState, useEffect } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { reportAPI } from '../services/api';
import { toast } from 'sonner';

export default function BreachReportPage() {
  const [data, setData] = useState({ total_breaches: 0, breaches: [] });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await reportAPI.slaBreach(params);
      setData(res.data || { total_breaches: 0, breaches: [] });
    } catch { setData({ total_breaches: 0, breaches: [] }); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, []);

  const handleExport = () => {
    if (!data.breaches?.length) { toast.error('No data to export'); return; }
    const headers = ['Ticket', 'Title', 'Priority', 'Status', 'Created', 'Response Due', 'Resolution Due', 'Response Met', 'Resolution Met'];
    const rows = data.breaches.map(b => [
      b.ticket_number, b.title, b.priority, b.status,
      b.created_at ? new Date(b.created_at).toLocaleString() : '',
      b.response_due ? new Date(b.response_due).toLocaleString() : '',
      b.resolution_due ? new Date(b.resolution_due).toLocaleString() : '',
      b.sla_response_met ? 'Yes' : 'No',
      b.sla_resolution_met ? 'Yes' : 'No',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sla_breach_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported as CSV');
  };

  return (
    <div data-testid="breach-report-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-rose-600" /> SLA Breach Report
        </h2>
        <button onClick={handleExport} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="export-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div>
          <label className="text-xs text-slate-500">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="block border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
        </div>
        <div>
          <label className="text-xs text-slate-500">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="block border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
        </div>
        <div className="flex items-end">
          <button onClick={fetch} className="bg-white border px-4 py-2 rounded-xl text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50">Apply Filters</button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-rose-500">
          <div className="text-2xl font-bold text-rose-600">{data.total_breaches}</div>
          <div className="text-xs text-slate-500">Total Breaches</div>
        </div>
        <div className="bg-white p-4 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-amber-500">
          <div className="text-2xl font-bold text-amber-600">{data.breaches?.filter(b => !b.sla_response_met).length || 0}</div>
          <div className="text-xs text-slate-500">Response Breaches</div>
        </div>
        <div className="bg-white p-4 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-orange-500">
          <div className="text-2xl font-bold text-orange-600">{data.breaches?.filter(b => !b.sla_resolution_met).length || 0}</div>
          <div className="text-xs text-slate-500">Resolution Breaches</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : data.breaches?.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No SLA breaches found for the selected period</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="breach-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Ticket</th>
                <th className="px-4 py-3 text-left font-semibold">Title</th>
                <th className="px-4 py-3 text-center font-semibold">Priority</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Response Met</th>
                <th className="px-4 py-3 text-center font-semibold">Resolution Met</th>
                <th className="px-4 py-3 text-center font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {data.breaches.map((b, i) => (
                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3 font-mono text-rose-700">{b.ticket_number}</td>
                  <td className="px-4 py-3">{b.title}</td>
                  <td className="px-4 py-3 text-center"><span className="bg-rose-100 text-rose-800 px-2 py-1 rounded-full text-xs font-semibold">{b.priority}</span></td>
                  <td className="px-4 py-3 text-center">{b.status?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-center">{b.sla_response_met === false ? <span className="text-rose-600 font-bold">No</span> : <span className="text-emerald-600">Yes</span>}</td>
                  <td className="px-4 py-3 text-center">{b.sla_resolution_met === false ? <span className="text-rose-600 font-bold">No</span> : <span className="text-emerald-600">Yes</span>}</td>
                  <td className="px-4 py-3 text-center text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
